import { Link } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { CATEGORIES, PRODUCTS, calcCommission, type Product } from '../lib/products'
import { useI18n } from '../lib/i18n'

const FEATURED_SLUGS = [
  'restaurant',
  'coffee-bar',
  'hair-salon',
  'car-rental',
  'dentist',
  'loyalty-cafe',
]

const PARTNER_SLUGS = [
  'barbershop',
  'restaurant',
  'car-rental',
  'tile-workers',
]

function formatEuro(value: number) {
  return `EUR ${value.toLocaleString('en-US', { maximumFractionDigits: value % 1 ? 2 : 0 })}`
}

function productHref(product: Product) {
  return `/catalog/${product.slug}`
}

function productDemoHref(product: Product) {
  if (!product.demoUrl) return productHref(product)
  return product.demoUrl.startsWith('http') ? product.demoUrl : product.demoUrl
}

export default function Home() {
  const { t } = useI18n()
  const liveCount = PRODUCTS.filter((p) => p.demoUrl).length
  const [query, setQuery] = useState('')

  const featured = useMemo(() => {
    const picked = FEATURED_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug)).filter(Boolean) as Product[]
    return picked.length >= 6 ? picked : PRODUCTS.slice(0, 6)
  }, [])

  const partnerShelf = useMemo(() => {
    const picked = PARTNER_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug)).filter(Boolean) as Product[]
    return picked.length ? picked : PRODUCTS.slice(0, 4)
  }, [])

  const visibleCategories = CATEGORIES.map((category) => ({
    ...category,
    count: PRODUCTS.filter((product) => product.category === category.slug).length,
  }))

  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return []
    return PRODUCTS.filter((product) =>
      `${product.name} ${product.tagline} ${product.whyNeed}`.toLowerCase().includes(term),
    ).slice(0, 5)
  }, [query])

  return (
    <div className="-mt-16 min-h-screen bg-[#eef2f8] pt-16 text-[#090f24]">
      <section className="border-b border-[#dfe5ef] bg-gradient-to-br from-[#f7f9fd] via-[#f0f4fb] to-[#f5efe6]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_520px] lg:px-8 lg:py-20">
          <div className="flex flex-col justify-center">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#dce4ef] bg-white px-3 py-2 text-xs font-extrabold text-[#60708d] shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              {liveCount}/{PRODUCTS.length} {t('live demos wired')}
            </div>

            <h1 className="mt-7 max-w-3xl text-[2.55rem] font-black leading-[0.98] tracking-[-0.02em] text-[#071023] sm:text-5xl lg:text-[3.7rem]">
              {t('AI automations your customers can')}{' '}
              <span className="text-[#ffb21f]">{t('try before they buy.')}</span>
            </h1>

            <p className="mt-7 max-w-2xl text-lg font-medium leading-8 text-[#65728d]">
              {t('A catalog of working AI products for real local businesses: booking, front-desk, ordering, quotes, loyalty, and lead capture. Test the live demo, show it on your phone, then submit the lead.')}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/catalog"
                className="inline-flex items-center justify-center rounded-xl bg-[#071023] px-6 py-4 text-sm font-black text-white shadow-lg shadow-slate-900/15 transition hover:-translate-y-0.5 hover:bg-[#111b34]"
              >
                {t('Browse the catalog')}
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-xl bg-[#ffb21f] px-6 py-4 text-sm font-black text-[#211300] shadow-lg shadow-amber-300/35 transition hover:-translate-y-0.5 hover:bg-[#ffa40b]"
              >
                {t('Earn as a partner →')}
              </Link>
              <Link
                to="/story"
                className="inline-flex items-center justify-center rounded-xl border border-[#dfe5ef] bg-white px-6 py-4 text-sm font-black text-[#071023] shadow-sm transition hover:-translate-y-0.5 hover:border-[#ffb21f]"
              >
                ▶ {t('Watch how you earn')} <span className="ml-1.5 text-[#8b96ab]">45s</span>
              </Link>
            </div>

            <div className="mt-9 grid max-w-2xl grid-cols-1 gap-4 divide-[#dbe3ee] sm:grid-cols-3 sm:divide-x sm:gap-0">
              <HeroMetric value={PRODUCTS.length.toString()} label={t('products')} />
              <HeroMetric value="30%" label={t('recurring commission')} accent />
              <HeroMetric value="6" label={t('languages')} />
            </div>
          </div>

          <div className="flex items-center">
            <DemoWindow product={featured[0] ?? PRODUCTS[0]} />
          </div>
        </div>
      </section>

      <section className="border-b border-[#dfe5ef] bg-white">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-4 text-sm font-black text-[#3d4659] sm:px-6 lg:grid-cols-[230px_1fr] lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[#95a0b3]">
            {t('Ready demo sectors')}
          </div>
          <div className="flex flex-wrap justify-between gap-x-8 gap-y-3">
            {visibleCategories.slice(0, 5).map((category) => (
              <Link key={category.slug} to={`/catalog?cat=${category.slug}`} className="hover:text-[#ff9f0a]">
                {t(category.name)}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-[#071023]">{t('Browse by industry')}</h2>
            <p className="mt-2 text-base font-medium text-[#697790]">
              {t('Every product ships with a live demo. No credit card to try.')}
            </p>
          </div>

          <div className="relative w-full max-w-sm">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9aa6b8]">⌕</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`${t('Search')} ${PRODUCTS.length} ${t('AI products...')}`}
              className="w-full rounded-xl border border-[#dfe5ef] bg-white py-3 pl-10 pr-16 text-sm font-semibold text-[#071023] shadow-sm outline-none transition focus:border-[#ffb21f] focus:ring-4 focus:ring-amber-200/60"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg border border-[#dfe5ef] px-2 py-1 text-xs font-black text-[#a1abbc]">
              /K
            </span>
            {searchResults.length > 0 && (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-20 w-full overflow-hidden rounded-2xl border border-[#dfe5ef] bg-white shadow-xl">
                {searchResults.map((product) => (
                  <Link
                    key={product.slug}
                    to={productHref(product)}
                    className="flex items-center gap-3 border-b border-[#eef2f7] px-4 py-3 last:border-b-0 hover:bg-[#f7f9fd]"
                  >
                    <span className="h-9 w-9 shrink-0 overflow-hidden rounded-xl border border-[#eaeef5] bg-[#eef2f8]">
                      {product.demoUrl ? (
                        <img src={`/previews/${product.slug}.jpg`} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
                      ) : (
                        <span className="grid h-full w-full place-items-center text-base">{product.icon}</span>
                      )}
                    </span>
                    <span>
                      <span className="block text-sm font-black text-[#071023]">{product.name}</span>
                      <span className="line-clamp-1 text-xs font-semibold text-[#77849a]">{t(product.tagline)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {visibleCategories.map((category) => (
            <Link
              key={category.slug}
              to={`/catalog?cat=${category.slug}`}
              className="group rounded-2xl border border-[#dfe5ef] bg-white p-5 text-center shadow-sm transition hover:-translate-y-1 hover:border-[#ffc04d] hover:shadow-xl hover:shadow-amber-100"
            >
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-[#ff4c7d] to-[#ffb21f] text-xl shadow-lg shadow-rose-100">
                {category.icon}
              </span>
              <span className="mt-4 block text-sm font-black text-[#071023] group-hover:text-[#da7c00]">
                {t(category.name)}
              </span>
              <span className="mt-1 block text-xs font-bold text-[#7f8aa0]">
                {category.count} {t(category.count === 1 ? 'product' : 'products')}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mb-7 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-[#071023]">{t('Featured products')}</h2>
            <p className="mt-2 text-base font-medium text-[#697790]">
              {t('Pick the product that matches the business owner in front of you.')}
            </p>
          </div>
          <Link to="/catalog" className="hidden text-sm font-black text-[#f59e0b] hover:text-[#d97706] sm:inline">
            {t('View all')} {PRODUCTS.length} →
          </Link>
        </div>

        <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
          {featured.map((product) => (
            <ProductTile key={product.slug} product={product} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#081226] via-[#12213d] to-[#303541] p-8 text-white shadow-2xl shadow-slate-300 sm:p-10 lg:p-12">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_480px] lg:items-center">
            <div>
              <span className="inline-flex rounded-full border border-[#ffb21f]/40 bg-[#ffb21f]/10 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#ffbf45]">
                {t('For partners')}
              </span>
              <h2 className="mt-6 max-w-3xl text-3xl font-black leading-tight tracking-tight sm:text-5xl">
                {t('Pick products you believe in. Share your link. Earn on every closed deal.')}
              </h2>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-[#b8c4d8]">
                {t('Free to join. You earn EUR 30 setup plus 30% recurring for 6 months when a business you refer subscribes. The missing part is traffic: outreach has not started yet.')}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-xl bg-[#ffb21f] px-6 py-4 text-sm font-black text-[#211300] shadow-xl shadow-amber-500/25 transition hover:-translate-y-0.5 hover:bg-[#ffa40b]"
                >
                  {t('Start earning - it is free →')}
                </Link>
                <Link
                  to="/partners/demo"
                  className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-4 text-sm font-black text-white transition hover:bg-white/10"
                >
                  {t('View demo storefront')}
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/8 p-5 backdrop-blur">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-[#9eacc5]">
                {t('Honest status panel')}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <DarkMetric value="0" label={t('subscribers')} />
                <DarkMetric value="0%" label={t('conversion')} />
                <DarkMetric value="4/wk" label={t('real traffic')} />
                <DarkMetric value="€0" label={t('to join as a partner')} />
              </div>
              <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-4">
                <div className="text-3xl font-black text-[#ffbf45]">{t('Next money move')}</div>
                <p className="mt-2 text-sm font-medium leading-6 text-[#d9e2f2]">
                  {t('Approve and contact the verified influencer list. Design polish helps, but outreach is what can create revenue.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-[#dfe5ef] bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-black text-[#071023]">{t('Partner storefront')}</h2>
            <p className="mt-3 text-sm font-medium leading-6 text-[#697790]">
              {t('This is the sales page a partner can send to a local owner. Demo code only, no fake earnings or customers.')}
            </p>
            <div className="mt-5 rounded-2xl bg-[#f3f6fb] p-4">
              <div className="text-xs font-black uppercase tracking-wider text-[#8591a8]">{t('Referral code')}</div>
              <div className="mt-1 font-mono text-2xl font-black text-[#071023]">QRD-DEMO-000</div>
            </div>
            <Link
              to="/partners/demo"
              className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-[#071023] px-5 py-4 text-sm font-black text-white transition hover:bg-[#111b34]"
            >
              {t('Open storefront')}
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {partnerShelf.map((product) => (
              <Link
                key={product.slug}
                to={productHref(product)}
                className="rounded-2xl border border-[#dfe5ef] bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-[#ffc04d] hover:shadow-xl hover:shadow-amber-100"
              >
                <span className="block aspect-[8/5] overflow-hidden rounded-xl border border-[#eaeef5] bg-[#eef2f8]">
                  {product.demoUrl ? (
                    <img src={`/previews/${product.slug}.jpg`} alt={`${product.name} preview`} loading="lazy" className="h-full w-full object-cover object-top" />
                  ) : (
                    <span className="grid h-full w-full place-items-center text-4xl">{product.icon}</span>
                  )}
                </span>
                <h3 className="mt-4 text-base font-black text-[#071023]">{product.name}</h3>
                <p className="mt-2 line-clamp-3 text-sm font-medium leading-6 text-[#697790]">
                  {t(product.tagline)}
                </p>
                <span className="mt-4 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                  {t('Live demo ready')}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm font-bold text-[#8a95a8] sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <div className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#ffb21f] text-sm font-black text-[#211300]">
              Q
            </span>
            <span>{t('© 2026 Qordo Catalog — AI automations for real businesses')}</span>
          </div>
          <div className="flex flex-wrap gap-5">
            <Link to="/catalog" className="hover:text-[#f59e0b]">{t('Catalog')}</Link>
            <Link to="/how-it-works" className="hover:text-[#f59e0b]">{t('Partners')}</Link>
            <Link to="/story" className="hover:text-[#f59e0b]">{t('Watch the story')}</Link>
            <Link to="/marketplace" className="hover:text-[#f59e0b]">{t('Marketplace')}</Link>
            <Link to="/signup" className="hover:text-[#f59e0b]">{t('Join')}</Link>
            <Link to="/terms" className="hover:text-[#f59e0b]">{t('Terms')}</Link>
            <Link to="/privacy" className="hover:text-[#f59e0b]">{t('Privacy')}</Link>
          </div>
        </div>
      </section>
    </div>
  )
}

function HeroMetric({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <div className="px-6 first:pl-0">
      <div className={`text-3xl font-black ${accent ? 'text-[#ff9f0a]' : 'text-[#071023]'}`}>{value}</div>
      <div className="mt-1 text-xs font-bold text-[#7f8aa0]">{label}</div>
    </div>
  )
}

function DemoWindow({ product }: { product: Product }) {
  const { t } = useI18n()
  const commission = calcCommission(product.plans.pro.price)

  return (
    <div className="w-full rounded-[1.5rem] border border-[#dfe5ef] bg-white p-4 shadow-2xl shadow-slate-300/70">
      <div className="flex items-center justify-between border-b border-[#edf1f6] pb-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f6d]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#ffb21f]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#45d0a1]" />
          <span className="ml-4 text-sm font-black text-[#7c879b]">{product.name} · {t('live demo')}</span>
        </div>
        <span className="flex items-center gap-1 text-xs font-black text-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          {t('LIVE')}
        </span>
      </div>

      <div className="space-y-4 px-1 py-5">
        <div className="max-w-[82%] rounded-2xl border border-[#e4e9f1] bg-[#fbfcff] p-4 text-sm font-semibold leading-6 text-[#23304a]">
          {t('Hi. I can show how')} {product.name.toLowerCase()} {t('works for your business in two minutes.')}
        </div>
        <div className="ml-auto w-fit max-w-[74%] rounded-2xl bg-[#ffbf45] px-4 py-3 text-sm font-black text-[#211300]">
          {t('Show me the demo')}
        </div>
        <div className="max-w-[88%] rounded-2xl border border-[#e4e9f1] bg-[#fbfcff] p-4 text-sm font-semibold leading-6 text-[#23304a]">
          {t('Done. The owner sees a working flow, pricing, and the exact problem it solves:')}
          {` ${t(product.whatSolves[0] ?? product.tagline)}`}
        </div>
      </div>

      <div className="grid gap-3 border-t border-[#edf1f6] pt-4 sm:grid-cols-[1fr_auto] sm:items-center">
        <div className="rounded-2xl bg-emerald-50 p-4">
          <div className="text-xs font-black uppercase tracking-wider text-emerald-700">{t('Partner payout if it closes')}</div>
          <div className="mt-1 text-2xl font-black text-emerald-800">{formatEuro(commission.total6m)}</div>
        </div>
        <a
          href={productDemoHref(product)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-xl border border-[#dfe5ef] px-5 py-4 text-sm font-black text-[#071023] transition hover:border-[#ffb21f] hover:bg-[#fff8eb]"
        >
          {t('Try the full demo →')}
        </a>
      </div>
    </div>
  )
}

function ProductTile({ product }: { product: Product }) {
  const { t } = useI18n()
  const commission = calcCommission(product.plans.pro.price)
  const live = !!product.demoUrl

  return (
    <article className="flex min-h-[280px] flex-col overflow-hidden rounded-2xl border border-[#dfe5ef] bg-white shadow-sm transition hover:-translate-y-1 hover:border-[#ffc04d] hover:shadow-xl hover:shadow-amber-100">
      {/* Real screenshot of the working product — not an initials tile */}
      <div className="relative aspect-[8/5] overflow-hidden border-b border-[#eaeef5] bg-[#eef2f8]">
        {live ? (
          <img
            src={`/previews/${product.slug}.jpg`}
            alt={`${product.name} live demo preview`}
            loading="lazy"
            onError={(e) => { (e.currentTarget.style.display = 'none') }}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <div className="grid h-full w-full place-items-center"><span className="text-5xl" aria-hidden>{product.icon}</span></div>
        )}
        {live && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-black text-emerald-700 border border-emerald-200 shadow-sm">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t('Live demo')}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate text-lg font-black text-[#071023]">{product.name}</h3>
          <span className="rounded-full border border-[#dfe5ef] px-2 py-0.5 text-[11px] font-black text-[#8792a5]">
            {product.category}
          </span>
        </div>

        <p className="mt-2 line-clamp-2 text-sm font-medium leading-6 text-[#53617a]">{t(product.tagline)}</p>

        <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs font-black text-emerald-700">
          ↗ {t('Partner earns')} {formatEuro(commission.total6m)} {t('if it closes')}
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <div className="text-lg font-black text-[#071023]">EUR {product.plans.pro.price}<span className="text-xs font-bold text-[#8b96ab]">{t('/mo')}</span></div>
          <div className="flex shrink-0 gap-2">
            {product.signupUrl && (
              <a
                href={product.signupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl bg-[#ffb21f] px-4 py-3 text-sm font-black text-[#211300] transition hover:bg-[#ffa40b]"
              >
                {t('Start free →')}
              </a>
            )}
            <Link
              to={productHref(product)}
              className="inline-flex items-center justify-center rounded-xl bg-[#071023] px-4 py-3 text-sm font-black text-white transition hover:bg-[#111b34]"
            >
              {t('Try demo')}
            </Link>
          </div>
        </div>
      </div>
    </article>
  )
}

function DarkMetric({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="text-3xl font-black text-white">{value}</div>
      <div className="mt-1 text-xs font-black uppercase tracking-wider text-[#9eacc5]">{label}</div>
    </div>
  )
}
